import React, { useEffect } from 'react';
import { Modal, Form, Input, DatePicker, Button } from 'antd';
import './AddProject.module.scss'; // Custom CSS to handle scroll and spacing
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import CustomSelectWithFilter from '../Common/CustomSelect/CustomSelectWithFilter';

const AddProject = ({ open, onCancel, onSubmit, initialData = null, setEditProject }) => {
        const [form] = Form.useForm();
        const isEditMode = initialData && Object.keys(initialData).length > 0;
        const { msCodeListing } = useSelector((state) => state.unitReport);

        const handleOk = async () => {
                try {
                        const values = await form.validateFields();
                        const formattedValues = {
                                ...values,
                                ZERO_DATE: values.ZERO_DATE?.format('DD-MM-YYYY') || null,
                                SCHEDULE_DATE: values.SCHEDULE_DATE?.format('DD-MM-YYYY') || null,
                                EXPECTED_DATE: values.EXPECTED_DATE?.format('DD-MM-YYYY') || null,
                                ACTUAL_DATE: values.ACTUAL_DATE?.format('DD-MM-YYYY') || null,
                        };

                        // ❗ Await onSubmit to know result
                        const result = await onSubmit(formattedValues);

                        if (result?.status === 'success') {
                                form.resetFields();
                                setEditProject({});
                        }
                        // ❗ Don't reset form or close modal unless it's successful
                } catch (err) {
                        console.log('Validation or submission error:', err);
                }
        };

        useEffect(() => {
                if (initialData && isEditMode) {
                        const formattedData = {
                                ...initialData,
                                ZERO_DATE: initialData.ZERO_DATE
                                        ? dayjs(initialData.ZERO_DATE, 'DD-MM-YYYY')
                                        : null,
                                SCHEDULE_DATE: initialData.SCHEDULE_DATE
                                        ? dayjs(initialData.SCHEDULE_DATE, 'DD-MM-YYYY')
                                        : null,
                                EXPECTED_DATE: initialData.EXPECTED_DATE
                                        ? dayjs(initialData.EXPECTED_DATE, 'DD-MM-YYYY')
                                        : null,
                                ACTUAL_DATE: initialData.ACTUAL_DATE
                                        ? dayjs(initialData.ACTUAL_DATE, 'DD-MM-YYYY')
                                        : null,
                        };

                        form.setFieldsValue(formattedData);
                } else {
                        form.resetFields();
                }
        }, [initialData, open]);

        return (
                <Modal
                        title={isEditMode ? 'Edit Project' : 'Add Project'}
                        open={open}
                        onCancel={() => {
                                form.resetFields();
                                onCancel();
                        }}
                        onOk={handleOk}
                        footer={[
                                <Button
                                        key="cancel"
                                        onClick={onCancel}>
                                        Cancel
                                </Button>,
                                <Button
                                        key="submit"
                                        type="primary"
                                        onClick={handleOk}>
                                        {isEditMode ? 'Update Project' : 'Add Project'}
                                </Button>,
                        ]}
                        width={600}>
                        <Form
                                form={form}
                                layout="vertical"
                                className="custom-form-scroll"
                                style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                                {[
                                        { label: 'Project Name', name: 'PROJ_NAME' },
                                        { label: 'IO Number', name: 'IO_NO' },
                                        { label: 'Rev No', name: 'REV_NO' },
                                        { label: 'PID', name: 'PID' },
                                        { label: 'Zero Date', name: 'ZERO_DATE', isDate: true },
                                        {
                                                label: 'Scheduled Date',
                                                name: 'SCHEDULE_DATE',
                                                isDate: true,
                                        },
                                        {
                                                label: 'Expected Date',
                                                name: 'EXPECTED_DATE',
                                                isDate: true,
                                        },
                                        { label: 'Actual Date', name: 'ACTUAL_DATE', isDate: true },
                                        { label: 'MW', name: 'MW' },
                                        { label: 'Unit No', name: 'UNIT_NO' },
                                        { label: 'P Type', name: 'PTYPE' },
                                        { label: 'Sub Type', name: 'SUB_TYPE' },
                                        { label: 'MS Code', name: 'MS_CODE' },
                                ].map(({ label, name, isDate }) => (
                                        <Form.Item
                                                key={name}
                                                name={name}
                                                rules={[
                                                        {
                                                                required: true,
                                                                message: `${label} is required`,
                                                        },
                                                ]}
                                                label={
                                                        <div className="flex justify-between items-center gap-2">
                                                                <label className=" text-left pr-4">
                                                                        {label}
                                                                        <span className="text-red-500">*</span>{' '}
                                                                </label>
                                                        </div>
                                                }
                                                labelCol={{ span: 7 }} // Roughly 40% in 24-grid system
                                                wrapperCol={{ span: 14 }} // Remaining 60%
                                                layout="horizontal"
                                                required={false} // ✅ Suppresses automatic red asterisk
                                        >
                                                {isDate ? (
                                                        <DatePicker
                                                                format="DD-MM-YY"
                                                                className="flex-1 w-full"
                                                        />
                                                ) : name === 'MS_CODE' ? (
                                                        <CustomSelectWithFilter
                                                                showSearch="true"
                                                                disabled={isEditMode}
                                                                data={
                                                                        Array.isArray(msCodeListing)
                                                                                ? msCodeListing.map((value) => ({
                                                                                          label: value?.MS_CODE,
                                                                                          value: value?.MS_CODE,
                                                                                  }))
                                                                                : []
                                                                }
                                                                placeholder="Select MS Code"
                                                                className="w-full custom-select-small"
                                                                onChange={(value) => form.setFieldValue('MS_CODE', value)}
                                                        />
                                                ) : (
                                                        <Input
                                                                className="flex-1"
                                                                disabled={isEditMode}
                                                        />
                                                )}
                                        </Form.Item>
                                ))}
                        </Form>
                </Modal>
        );
};

export default AddProject;
